import type { Dial } from 'nexusui';
import type { Settings } from '../state/settings';
import * as store from '../state/store';
import type { SettingKey } from '../state/store';

/**
 * Declarative two-way binding between a control and one settings key.
 *
 * The previous wiring was imperative in both directions and needed a
 * module-level `syncing` flag, because writing state into the DOM fires change
 * events that write straight back. Handlers here are *idempotent* instead: a
 * handler whose parsed value already equals the stored one returns before
 * touching anything. Writing state into the DOM therefore settles in one pass
 * with no flag to forget, and side effects such as invalidating the preset only
 * run on a real change.
 */

type FieldEl = HTMLInputElement | HTMLSelectElement;

export interface FieldSpec<K extends SettingKey> {
  readonly el: FieldEl;
  readonly key: K;
  /** How to read and write the element. Defaults to the element's own type. */
  readonly kind?: 'text' | 'number' | 'checkbox';
  /** Event to listen on. Defaults to 'input' for numbers, 'change' otherwise. */
  readonly event?: 'input' | 'change';
  /**
   * Drop focus after a change. Playing is suppressed while a form control has
   * focus, so a select that keeps it silently kills the computer keyboard.
   */
  readonly blurOnChange?: boolean;
  /** Switch the preset selector to "custom" when the user changes this. */
  readonly marksCustom?: boolean;
  /** Run after the store has been updated with a genuinely new value. */
  readonly onChange?: (value: Settings[K]) => void;
}

export interface KnobSpec<K extends SettingKey> {
  readonly dial: Dial;
  readonly key: K;
  /** The element the dial renders into; made focusable and ARIA-described. */
  readonly host: HTMLElement;
  readonly label: string;
  /** Dial-space bounds (what the physical knob sweeps and the arrow keys step). */
  readonly min: number;
  readonly max: number;
  readonly step: number;
  /**
   * Optional non-linear mapping between the dial position and the stored value.
   * The dial sweeps [min, max]; the store keeps `toStored(pos)`. Used to give
   * the filter a logarithmic (perceptual) response while persisting Hz.
   */
  readonly map?: { toStored: (dialPos: number) => number; toDial: (stored: number) => number };
  readonly readout?: HTMLElement;
  /** Visible readout text; receives the STORED value. */
  readonly format?: (value: number) => string;
  /** Spoken value; falls back to `format`. Receives the STORED value. */
  readonly valueText?: (value: number) => string;
  readonly marksCustom?: boolean;
  /** Always runs, including on programmatic writes, so audio tracks the knob. */
  readonly onChange?: (value: number) => void;
}

export interface Binder {
  field<K extends SettingKey>(spec: FieldSpec<K>): void;
  knob<K extends SettingKey>(spec: KnobSpec<K>): void;
  /** Push the whole of state into every bound control. */
  sync(): void;
}

function defaultKind(el: FieldEl): 'text' | 'number' | 'checkbox' {
  if (el instanceof HTMLInputElement) {
    if (el.type === 'checkbox') return 'checkbox';
    if (el.type === 'number' || el.type === 'range') return 'number';
  }
  return 'text';
}

export function createBinder(onCustom: () => void): Binder {
  const writers: Array<(s: Settings) => void> = [];

  return {
    field<K extends SettingKey>(spec: FieldSpec<K>): void {
      const kind = spec.kind ?? defaultKind(spec.el);
      const event = spec.event ?? (kind === 'number' ? 'input' : 'change');

      const read = (): Settings[K] => {
        if (kind === 'checkbox') return (spec.el as HTMLInputElement).checked as Settings[K];
        if (kind === 'number') return Number(spec.el.value) as Settings[K];
        return spec.el.value as Settings[K];
      };

      spec.el.addEventListener(event, () => {
        const value = read();
        // Idempotent: a value identical to the stored one is a programmatic
        // echo, not a user action, so nothing below should run.
        if (Object.is(store.get(spec.key), value)) return;
        store.update({ [spec.key]: value } as Partial<Settings>);
        if (spec.marksCustom) onCustom();
        spec.onChange?.(value);
      });

      if (spec.blurOnChange) spec.el.addEventListener('change', () => spec.el.blur());

      writers.push((s) => {
        const value = s[spec.key];
        if (kind === 'checkbox') (spec.el as HTMLInputElement).checked = Boolean(value);
        else spec.el.value = String(value);
      });
    },

    knob<K extends SettingKey>(spec: KnobSpec<K>): void {
      // NexusUI dials are mouse-only and render a bare <svg> that exposes
      // nothing to assistive tech, so the host is promoted to a real slider.
      const { host } = spec;
      const toStored = spec.map?.toStored ?? ((p: number) => p);
      const toDial = spec.map?.toDial ?? ((v: number) => v);
      const fmt = spec.format ?? String;
      const say = spec.valueText ?? spec.format ?? String;

      host.setAttribute('role', 'slider');
      host.setAttribute('tabindex', '0');
      host.setAttribute('aria-label', spec.label);
      host.setAttribute('aria-valuemin', String(toStored(spec.min)));
      host.setAttribute('aria-valuemax', String(toStored(spec.max)));

      // `stored` is the persisted value; the dial position may map to it non-
      // linearly. Readouts, ARIA and onChange all speak in stored units.
      const reflect = (stored: number): void => {
        if (spec.readout) spec.readout.textContent = fmt(stored);
        host.setAttribute('aria-valuenow', String(stored));
        host.setAttribute('aria-valuetext', say(stored));
      };

      // Assigning dial.value below re-emits 'change'; with a non-linear map the
      // round-trip rounding differs from the stored value, which would nudge
      // the setting and mark the preset custom. This flag ignores that echo.
      let echoing = false;

      spec.dial.on('change', (pos: number) => {
        const stored = toStored(pos);
        reflect(stored);
        // The audio parameter follows the dial unconditionally, so restoring
        // settings also reaches the graph.
        spec.onChange?.(stored);
        if (echoing || Object.is(store.get(spec.key), stored)) return;
        store.update({ [spec.key]: stored } as Partial<Settings>);
        if (spec.marksCustom) onCustom();
      });

      host.addEventListener('keydown', (e) => {
        const coarse = (spec.max - spec.min) / 10;
        let next: number | null = null;
        switch (e.key) {
          case 'ArrowLeft':
          case 'ArrowDown':
            next = spec.dial.value - spec.step;
            break;
          case 'ArrowRight':
          case 'ArrowUp':
            next = spec.dial.value + spec.step;
            break;
          case 'PageDown':
            next = spec.dial.value - coarse;
            break;
          case 'PageUp':
            next = spec.dial.value + coarse;
            break;
          case 'Home':
            next = spec.min;
            break;
          case 'End':
            next = spec.max;
            break;
          default:
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        spec.dial.value = Math.min(spec.max, Math.max(spec.min, next));
      });

      writers.push((s) => {
        const stored = Number(s[spec.key]);
        const pos = toDial(stored);
        if (!Object.is(spec.dial.value, pos)) {
          echoing = true;
          spec.dial.value = pos;
          echoing = false;
        }
        reflect(stored); // authoritative: overrides any echo readout
      });
    },

    sync(): void {
      const s = store.getSettings() as Settings;
      for (const write of writers) write(s);
    },
  };
}
